import OpenAI from 'openai';
import { supabase } from '../lib/supabase';
import { buyerOrdersService, buyerFavoritesService } from './buyerService';

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

/**
 * Analyzes buyer's purchase history and generates coffee bean recommendations
 * @param {string} language - User's preferred language ('en' or 'zh')
 * @returns {Promise<Object>} Recommendations with analysis and suggested beans
 */
export async function generateCoffeeBeanRecommendations(language = 'en') {
  try {
    // Get authenticated user
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Fetch buyer's purchase history
    const orders = await buyerOrdersService?.getAll();
    const favorites = await buyerFavoritesService?.getAll();

    // Get buyer profile for budget info
    const { data: profile } = await supabase
      ?.from('user_profiles')
      ?.select('*')
      ?.eq('id', user?.id)
      ?.single();

    // Fetch all available beans for recommendations
    const { data: allBeans } = await supabase
      ?.from('beans')
      ?.select(`
        id, name_en, name_zh, price_usd_per_kg, available_qty_kg, 
        processing_type, grade, certifications, cupping_notes_en, cupping_notes_zh,
        altitude_m, sourcing_region,
        country:countries(name_en, name_zh, code),
        supplier:suppliers(name_en, name_zh)
      `)
      ?.gt('available_qty_kg', 0)
      ?.order('created_at', { ascending: false })
      ?.limit(50);

    // Prepare purchase history summary
    const purchaseHistory = orders?.map(order => ({
      beanName: language === 'en' ? order?.bean?.nameEn : order?.bean?.nameZh,
      quantityKg: order?.quantityKg,
      pricePerKg: order?.bean?.priceUsdPerKg,
      supplier: language === 'en' ? order?.bean?.supplier?.nameEn : order?.bean?.supplier?.nameZh,
      date: order?.createdAt
    }));

    const favoritesList = favorites?.map(fav => ({
      beanName: language === 'en' ? fav?.bean?.nameEn : fav?.bean?.nameZh,
      pricePerKg: fav?.bean?.priceUsdPerKg,
      processingType: fav?.bean?.processingType,
      grade: fav?.bean?.grade
    }));

    // Calculate budget patterns
    const avgSpending = orders?.length > 0
      ? orders?.reduce((sum, order) => sum + (order?.quantityKg * order?.bean?.priceUsdPerKg), 0) / orders?.length
      : 0;

    const avgPricePerKg = orders?.length > 0
      ? orders?.reduce((sum, order) => sum + order?.bean?.priceUsdPerKg, 0) / orders?.length
      : 0;

    // Prepare available beans for AI analysis
    const availableBeans = allBeans?.map(bean => ({
      id: bean?.id,
      name: language === 'en' ? bean?.name_en : bean?.name_zh,
      pricePerKg: bean?.price_usd_per_kg,
      availableQty: bean?.available_qty_kg,
      processingType: bean?.processing_type,
      grade: bean?.grade,
      certifications: bean?.certifications,
      cuppingNotes: language === 'en' ? bean?.cupping_notes_en : bean?.cupping_notes_zh,
      altitude: bean?.altitude_m,
      region: bean?.sourcing_region,
      country: language === 'en' ? bean?.country?.name_en : bean?.country?.name_zh,
      supplier: language === 'en' ? bean?.supplier?.name_en : bean?.supplier?.name_zh
    }));

    // Create prompt for OpenAI
    const systemPrompt = language === 'en' ?'You are an expert coffee buyer consultant. Analyze the buyer\'s purchase history, favorite beans, and budget patterns to recommend the best matching coffee beans from available inventory. Focus on sourcing patterns, quality preferences, and budget alignment.' :'您是一位专业的咖啡采购顾问。分析买家的购买历史、收藏的咖啡豆和预算模式，从可用库存中推荐最匹配的咖啡豆。重点关注采购模式、质量偏好和预算匹配。';

    const userPrompt = language === 'en' ? `Analyze this buyer's coffee purchasing patterns and recommend 5 best matching beans:

**Purchase History (${orders?.length} orders):**
${JSON.stringify(purchaseHistory, null, 2)}

**Favorite Beans (${favorites?.length} items):**
${JSON.stringify(favoritesList, null, 2)}

**Budget Patterns:**
- Average spending per order: $${avgSpending?.toFixed(2)}
- Average price per kg: $${avgPricePerKg?.toFixed(2)}

**Available Beans:**
${JSON.stringify(availableBeans, null, 2)}

Provide recommendations in this exact JSON format:
{
  "analysis": "Brief analysis of buyer's preferences and patterns",
  "recommendations": [
    {
      "beanId": "bean ID from available beans",
      "matchScore": 95,
      "reason": "Why this bean matches their preferences",
      "budgetFit": "within_budget | slightly_above | premium_option"
    }
  ],
  "insights": [
    "Key insight about their sourcing pattern",
    "Budget recommendation",
    "Quality preference observation"
  ]
}`
      : `分析这位买家的咖啡采购模式并推荐5款最匹配的咖啡豆：

**购买历史（${orders?.length}个订单）：**
${JSON.stringify(purchaseHistory, null, 2)}

**收藏的咖啡豆（${favorites?.length}项）：**
${JSON.stringify(favoritesList, null, 2)}

**预算模式：**
- 每单平均支出：$${avgSpending?.toFixed(2)}
- 每公斤平均价格：$${avgPricePerKg?.toFixed(2)}

**可用咖啡豆：**
${JSON.stringify(availableBeans, null, 2)}

请以此JSON格式提供推荐：
{
  "analysis": "买家偏好和模式的简要分析",
  "recommendations": [
    {
      "beanId": "可用咖啡豆的ID",
      "matchScore": 95,
      "reason": "为什么这款咖啡豆符合他们的偏好",
      "budgetFit": "within_budget | slightly_above | premium_option"
    }
  ],
  "insights": [
    "关于采购模式的关键见解",
    "预算建议",
    "质量偏好观察"
  ]
}`;

    // Call OpenAI API
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'coffee_recommendations',
          schema: {
            type: 'object',
            properties: {
              analysis: { type: 'string' },
              recommendations: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    beanId: { type: 'string' },
                    matchScore: { type: 'number' },
                    reason: { type: 'string' },
                    budgetFit: { type: 'string' }
                  },
                  required: ['beanId', 'matchScore', 'reason', 'budgetFit']
                }
              },
              insights: {
                type: 'array',
                items: { type: 'string' }
              }
            },
            required: ['analysis', 'recommendations', 'insights'],
            additionalProperties: false
          }
        }
      },
      temperature: 0.7,
      max_tokens: 2000
    });

    const result = JSON.parse(response?.choices?.[0]?.message?.content);

    // Enrich recommendations with full bean data
    const enrichedRecommendations = result?.recommendations?.map(rec => {
      const bean = allBeans?.find(b => b?.id === rec?.beanId);
      return {
        ...rec,
        bean: bean ? {
          id: bean?.id,
          nameEn: bean?.name_en,
          nameZh: bean?.name_zh,
          priceUsdPerKg: bean?.price_usd_per_kg,
          availableQtyKg: bean?.available_qty_kg,
          processingType: bean?.processing_type,
          grade: bean?.grade,
          certifications: bean?.certifications,
          cuppingNotesEn: bean?.cupping_notes_en,
          cuppingNotesZh: bean?.cupping_notes_zh,
          altitudeM: bean?.altitude_m,
          sourcingRegion: bean?.sourcing_region,
          country: {
            nameEn: bean?.country?.name_en,
            nameZh: bean?.country?.name_zh,
            code: bean?.country?.code
          },
          supplier: {
            nameEn: bean?.supplier?.name_en,
            nameZh: bean?.supplier?.name_zh
          }
        } : null
      };
    })?.filter(rec => rec?.bean !== null);

    return {
      analysis: result?.analysis,
      recommendations: enrichedRecommendations,
      insights: result?.insights,
      metadata: {
        totalOrders: orders?.length,
        totalFavorites: favorites?.length,
        avgSpending: avgSpending?.toFixed(2),
        avgPricePerKg: avgPricePerKg?.toFixed(2),
        generatedAt: new Date()?.toISOString()
      }
    };
  } catch (error) {
    console.error('Error generating recommendations:', error);
    throw new Error(error?.message || 'Failed to generate recommendations');
  }
}

export default { generateCoffeeBeanRecommendations };